/**
 * emailHygieneReconciliationApi
 *
 * DASH-EMAIL-013: client for the real, provider-neutral Email Hygiene
 * Reconciliation API (EMAIL-HYGIENE-RECON-002/003,
 * `/api/email-hygiene/reconciliations*`).
 *
 * Wraps the SAME shared `api` axios instance every other real-backed War
 * Room surface already uses (see lib/api.js, emailMarketingApi.js) — no
 * second HTTP stack. Auth (Bearer token) and tenant scoping (X-Account-ID
 * header) are attached automatically by that instance's own request
 * interceptor.
 *
 * This is the ENDURING "Email Hygiene Reconciliation" contract, not a
 * SendGrid-specific one — every field this module returns is whatever the
 * backend's provider-neutral response models actually contain (`provider`
 * is a plain string, `provider_specific_evidence` an opaque object). This
 * module does not special-case "sendgrid" and must not be extended to
 * assume SES fields/shapes when that provider is added later.
 *
 * Query params for `listEmailHygieneReconciliations` map 1:1 to the real
 * RECON-002 route's actual supported filters — environment, reason,
 * recipient_email, limit, offset. No frontend-only filters (e.g. a
 * provider filter, or fuzzy recipient search) are introduced here; the
 * backend only supports an exact normalized-email match and has no
 * provider filter today.
 */
import { api } from "./api";

export async function listEmailHygieneReconciliations({
  environment,
  reason,
  recipientEmail,
  recoveryState,
  limit = 25,
  offset = 0,
} = {}) {
  const params = { limit, offset };
  if (environment && environment !== "all") params.environment = environment;
  if (reason && reason !== "all") params.reason = reason;
  if (recipientEmail) params.recipient_email = recipientEmail;
  // EMAIL-HYGIENE-RECOVERY-STATE-001 (Phase 1): Native-only derived filter.
  // The backend applies it BEFORE pagination and reports totals for the
  // filtered set; selecting one necessarily excludes Production Sync rows.
  if (recoveryState && recoveryState !== "all") params.recovery_state = recoveryState;
  const res = await api.get("/email-hygiene/reconciliations", { params });
  return res.data;
}

/** Returns null on 404 (reconciliation resolved-and-since-changed /
 * not found / cross-tenant) rather than throwing — callers distinguish
 * "not found" from a genuine request error by null vs. a rejected
 * promise, matching emailMarketingApi.js's getCreativeDetail convention. */
export async function getEmailHygieneReconciliation(reconciliationId) {
  try {
    const res = await api.get(`/email-hygiene/reconciliations/${encodeURIComponent(reconciliationId)}`);
    return res.data;
  } catch (err) {
    if (err?.response?.status === 404) return null;
    throw err;
  }
}

/**
 * executeEmailHygieneReconciliationAction
 *
 * Calls the real RECON-003 governed execute route. `actionType` must be
 * one of the backend-returned `available_actions[].action_type` values —
 * this module never derives or invents one. The request body is
 * deliberately narrow (only `resolution_reason`), matching the real
 * ExecuteReconciliationActionRequest contract exactly: no candidate
 * state, no fencing values, no GHL payload is ever sent from the
 * frontend — the backend derives and revalidates all of that itself.
 */
export async function executeEmailHygieneReconciliationAction(reconciliationId, actionType, { resolutionReason }) {
  const res = await api.post(
    `/email-hygiene/reconciliations/${encodeURIComponent(reconciliationId)}/actions/${encodeURIComponent(actionType)}`,
    { resolution_reason: resolutionReason }
  );
  return res.data;
}

/** Same pure-function shape as emailMarketingApi.js's own
 * extractApiErrorMessage — reproduced locally rather than imported
 * cross-module so this domain's API client stays self-contained. */
export function extractApiErrorMessage(err, fallback) {
  return err?.response?.data?.detail || err?.message || fallback;
}
